import { GraphQLResult } from '@aws-amplify/api-graphql';
import { Signer } from '@aws-amplify/core';
import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { HighnoteValidationError, highnoteFieldErrorsOf } from 'app/services/highnoteValidationError';
import { captureException } from '@sentry/react';

import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';
import {
  getCreditLimitAsSupportQuery,
  pullCreditLimitFromHighnoteMutation,
  setCreditLimitAsSupportMutation,
} from 'graphql_custom/control-center/creditLimit';
import { listAuditLogEventsQuery } from 'graphql_custom/team/auditLog';
// SEE-1232 row n: the same document serves both APIs — CDK's `getPaidol(id: ID!): Paidol`
// has the identical signature and every field this selection reads. Imported rather than
// copied because it is ~120 lines with fragments, and because SEE-862 deliberately kept it
// in `graphql_custom` in one place so this migration would touch one file.
import { getPaidolForSwitcher } from 'graphql_custom/queries';
import type {
  AuditLogEventPage,
  ListAuditLogEventsInput,
  BindOnboardingPhoneInput,
  GetJobCodesInput,
  GetJobCodesPayloadResult,
  GetOnboardingDraftInput,
  OnboardingDraftPayload as GeneratedOnboardingDraftPayload,
  OnboardingProduct,
  OnboardingDraftSaveResult,
  OnboardingSessionChallenge,
  OnboardingSessionVerifyResult,
  RegisterOnboardInput,
  SaveOnboardingDraftInput,
  StartOnboardingSessionInput as GeneratedStartOnboardingSessionInput,
  SupportCreditLimit,
  VerifyOnboardingCodeInput,
} from 'CDK_API';

// Re-export types used by other files
export type {
  BindOnboardingPhoneInput,
  GetJobCodesInput,
  GetJobCodesPayloadResult,
  GetOnboardingDraftInput,
  OnboardingDraftFormDataInput,
  OnboardingDraftSaveResult,
  OnboardingProduct,
  OnboardingSessionChallenge,
  OnboardingSessionVerifyResult,
  RegisterOnboardInput,
  SaveOnboardingDraftInput,
  VerifyOnboardingCodeInput,
} from 'CDK_API';

/**
 * `confirmPhone` was removed from the schema in cdk `dbe40bd2`. AppSync validates input fields
 * against the schema and rejects the whole request *before the resolver runs*, so sending it is a
 * hard break rather than an ignored extra.
 *
 * `CDK_API.ts` is generated and still carries the field until it is regenerated (which needs the
 * `../speedchain-cdk` checkout). Narrowing here means a stray `confirmPhone` fails to compile instead
 * of failing in front of an applicant, and the `Pick` keeps this tied to the generated type rather
 * than hand-copying it.
 */
export type StartOnboardingSessionInput = Pick<GeneratedStartOnboardingSessionInput, 'invitationCode'>;

/**
 * The mirror image of the above: `rebatePercentage` is live on the deployed payload as of cdk
 * `ff976a88`, but `CDK_API.ts` predates it. Widened here so the field can be read without a cast.
 *
 * Both of these local adjustments disappear the moment `yarn codegen:cdk` can run — that needs the
 * `../speedchain-cdk` checkout, which is deliberately absent. They are stopgaps for a stale generated
 * file, not a pattern to keep extending.
 */
export type OnboardingDraftPayload = GeneratedOnboardingDraftPayload & {
  rebatePercentage?: number | null;
};

type AuthMode = 'cognito' | 'iam';

// --- GraphQL Operations ---

const registerOnboardMutation = /* GraphQL */ `
  mutation RegisterOnboard($input: RegisterOnboardInput!) {
    registerOnboard(input: $input)
  }
`;

// SEE-1274 onboarding OTP session. Every one of these runs pre-auth: the applicant has no Cognito
// account, so `execute` is called with 'iam' and signs with the Cognito *unauthenticated*
// identity-pool credentials. For the two draft operations the session grant in the payload is what
// authorises the call; 'iam' only gets the request past AppSync.

const startOnboardingSessionMutation = /* GraphQL */ `
  mutation StartOnboardingSession($input: StartOnboardingSessionInput!) {
    startOnboardingSession(input: $input) {
      status
      maskedPhone
      expiresInSeconds
      resendAvailableInSeconds
    }
  }
`;

const bindOnboardingPhoneMutation = /* GraphQL */ `
  mutation BindOnboardingPhone($input: BindOnboardingPhoneInput!) {
    bindOnboardingPhone(input: $input) {
      status
      maskedPhone
      expiresInSeconds
      resendAvailableInSeconds
    }
  }
`;

const verifyOnboardingCodeMutation = /* GraphQL */ `
  mutation VerifyOnboardingCode($input: VerifyOnboardingCodeInput!) {
    verifyOnboardingCode(input: $input) {
      verified
      attemptsRemaining
      reason
      grant
      expiresInSeconds
    }
  }
`;

// `middleName` and `suffix` are selected here deliberately. The Amplify document this replaces
// (`listOnboardByInvitationCode`) omitted both while the save selected them, so resuming a draft
// dropped them on the next save (Req 3.4).
const getOnboardingDraftQuery = /* GraphQL */ `
  query GetOnboardingDraft($input: GetOnboardingDraftInput!) {
    getOnboardingDraft(input: $input) {
      found
      invitationCode
      # Drives the issuingPermission consent label. Reached the form via the Amplify
      # listOnboardByInvitationCode query before the CDK cutover; omitting it here is what made
      # every applicant tick a "no fees" box against a 1.5% rebate.
      rebatePercentage
      formData {
        organizationProfile {
          productId
          legalBusinessName
          doingBusinessAs
          phoneNumber {
            countryCode
            number
            extension
          }
          phoneNumberValue
          hasExtension
          phoneExtension
          businessType
          annualRevenue
          businessIndustryClassification {
            businessId
            businessName
            industryId
            industryName
          }
          billingAddress {
            address
            extendedAddress
            city
            state
            zip
            country
          }
          identification
        }
        primaryAuthorizedPerson {
          firstName
          middleName
          lastName
          suffix
          email
          birthdate
          socialSecurityNumber
          jobTitle
          phoneNumber {
            countryCode
            number
            extension
          }
          phoneNumberValue
          hasExtension
          phoneExtension
          percentageOwnership
          homeAddress {
            address
            extendedAddress
            city
            state
            zip
            country
          }
          primaryAuthorizedPersonConsent {
            cardholderAgreementPermission
            issuingPermission
            signature
          }
        }
        beneficialOwners {
          firstName
          middleName
          lastName
          suffix
          email
          birthdate
          socialSecurityNumber
          phoneNumber {
            countryCode
            number
            extension
          }
          phoneNumberValue
          hasExtension
          phoneExtension
          percentageOwnership
          homeAddress {
            address
            extendedAddress
            city
            state
            zip
            country
          }
        }
        administrator {
          firstName
          lastName
          email
        }
        informationCertification
      }
    }
  }
`;

// Anonymous and input-less. Returns only active products; `isActive` is filtered server-side and
// deliberately absent from the payload, so there is nothing to filter client-side.
const listProductsQuery = /* GraphQL */ `
  query ListProducts {
    listProducts {
      id
      type
    }
  }
`;

const saveOnboardingDraftMutation = /* GraphQL */ `
  mutation SaveOnboardingDraft($input: SaveOnboardingDraftInput!) {
    saveOnboardingDraft(input: $input) {
      success
      statusCode
      message
    }
  }
`;

const inviteUserMutation = /* GraphQL */ `
  mutation InviteUser($input: InviteUserToPaidol!, $hostname: String!, $highnotePaymentCardId: String) {
    inviteUser(input: $input, hostname: $hostname, highnotePaymentCardId: $highnotePaymentCardId) {
      id
      paidol_id
      email
      roles
      user_id
      employee_id
      authorizedUserId
      authorizedUserApplicationId
      highnotePaymentCardId
      createdAt
      updatedAt
      deletedAt
    }
  }
`;

const createOrGetPaidolSessionMutation = /* GraphQL */ `
  mutation CreateOrGetPaidolSession($input: CreateOrGetPaidolSessionInput!) {
    createOrGetPaidolSession(input: $input) {
      statusCode
      body
    }
  }
`;

const generateStatementsMutation = /* GraphQL */ `
  mutation GenerateStatements($input: GenerateStatementInput!) {
    generateStatements(input: $input) {
      s3_key
      paidol_id
      period_start
      period_end
      message
      error
    }
  }
`;

const setThresholdAmountMutation = /* GraphQL */ `
  mutation SetThresholdAmount($input: SetThresholdAmount!) {
    setThresholdAmount(input: $input) {
      id
      paidol_id
      email
      roles
      user_id
      employee_id
      authorizedUserId
      authorizedUserApplicationId
      highnotePaymentCardId
      createdAt
      updatedAt
      deletedAt
    }
  }
`;

const checkPaidolSessionQuery = /* GraphQL */ `
  query CheckPaidolSession($input: CheckPaidolSessionInput!) {
    checkPaidolSession(input: $input) {
      statusCode
      message
    }
  }
`;

const v2SearchStatementSummariesQuery = /* GraphQL */ `
  query V2SearchStatementSummaries($input: SearchStatementSummariesInput!) {
    v2SearchStatementSummaries(input: $input) {
      items {
        id
        paidolId
        startDate
        endDate
        statementBalance
        paymentDueDate
        pdfLink
        createdAt
        updatedAt
      }
      nextToken
      total
    }
  }
`;

// SEE-1232 row o. Source: src/graphql_custom/auto-payments/queries.ts + mutations.ts.
// Field names and argument shapes are identical on both APIs; the only change is that
// CDK's CreateAutoPaymentInput has no `authPaidolId` (the server derives the tenant key),
// and neither mutation accepts Amplify's `$condition`.
const listAutoPaymentsByPaidolIdQuery = /* GraphQL */ `
  query ListAutoPaymentsByPaidolId($paidolId: ID!) {
    listAutoPaymentsByPaidolId(paidolId: $paidolId) {
      items {
        id
        paidolId
        amountType
        fixedAmountCents
        paymentDayOfMonth
        externalBankAccountId
        financialAccountId
        active
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const createAutoPaymentMutation = /* GraphQL */ `
  mutation CreateAutoPayment($input: CreateAutoPaymentInput!) {
    createAutoPayment(input: $input) {
      id
      paidolId
      amountType
      fixedAmountCents
      paymentDayOfMonth
      externalBankAccountId
      financialAccountId
      active
      createdBy
      updatedBy
      createdAt
      updatedAt
    }
  }
`;

const updateAutoPaymentMutation = /* GraphQL */ `
  mutation UpdateAutoPayment($input: UpdateAutoPaymentInput!) {
    updateAutoPayment(input: $input) {
      id
      paidolId
      amountType
      fixedAmountCents
      paymentDayOfMonth
      externalBankAccountId
      financialAccountId
      active
      createdBy
      updatedBy
      createdAt
      updatedAt
    }
  }
`;

// SEE-1232 row p. Source: src/graphql/queries.ts (getPaidolHighnoteIntegration) and
// src/graphql_custom/mutations.js (customUpdatePaidolHighnoteIntegration). The mutation
// drops Amplify's `$condition` argument, which CDK does not accept; the selection is
// unchanged and every field exists on the CDK PaidolHighnoteIntegration type.
const getPaidolHighnoteIntegrationQuery = /* GraphQL */ `
  query GetPaidolHighnoteIntegration($id: ID!) {
    getPaidolHighnoteIntegration(id: $id) {
      id
      paidolId
      userId
      legalBusinessName
      businessAccountHolderId
      primaryAuthorizedPersonId
      financialAccountId
      externalFinancialBankAccountId
      hasInitialFunding
      accountHolderCardProductApplicationId
      isApplicationAccepted
      isDisconnected
      highnoteProductId
      highnoteProduct {
        id
        type
        isActive
        createdAt
        updatedAt
      }
      finicityCustomerId
      dwollaCustomerId
      cardProfileId
      createdAt
      updatedAt
    }
  }
`;

const updatePaidolHighnoteIntegrationMutation = /* GraphQL */ `
  mutation UpdatePaidolHighnoteIntegration($input: UpdatePaidolHighnoteIntegrationInput!) {
    updatePaidolHighnoteIntegration(input: $input) {
      id
      paidolId
      userId
      legalBusinessName
      businessAccountHolderId
      primaryAuthorizedPersonId
      financialAccountId
      externalFinancialBankAccountId
      hasInitialFunding
      accountHolderCardProductApplicationId
      isApplicationAccepted
      isDisconnected
      highnoteProductId
      highnoteProduct {
        id
        type
        isActive
        createdAt
        updatedAt
      }
      finicityCustomerId
      dwollaCustomerId
      cardProfileId
      createdAt
      updatedAt
    }
  }
`;

// SEE-1232 row n. Source: src/app/pages/store/queries.ts (getPaidolAgaveToken).
// Two fields, so it is inlined here per this file's convention; the 50-field
// getPaidolForSwitcher document is imported instead of copied — see getPaidol() below.
const getPaidolAgaveTokenQuery = /* GraphQL */ `
  query GetPaidolAgaveToken($id: ID!) {
    getPaidol(id: $id) {
      id
      agaveAccountToken
    }
  }
`;

const getPriorityActionsQuery = /* GraphQL */ `
  query GetPriorityActions($input: PriorityActionsInput!) {
    getPriorityActions(input: $input) {
      newMessages
      inactiveCards
      unactivatedCards
      reimbursements
      missingJobCodes
      missingReceipts
      missingNotes
    }
  }
`;

const v2PaidolHealthCheckQuery = /* GraphQL */ `
  query V2PaidolHealthCheck($input: String) {
    v2PaidolHealthCheck(input: $input)
  }
`;

// SEE-857 D3 — admin-only mutation that replaces auto-gen updatePaidol for the
// settings surface (fuel capture, card lockout, ERP endpoint, receipt
// requirements, coding columns, GL/project/location visibility filters,
// Agave integration tokens, etc.). Returns BasicHTTPResponse where `body` is
// the JSON-stringified updated Paidol dict on 200 or an error string on 4xx.
const updatePaidolSettingsMutation = /* GraphQL */ `
  mutation UpdatePaidolSettings($input: UpdatePaidolSettingsInput!) {
    updatePaidolSettings(input: $input) {
      statusCode
      body
    }
  }
`;

const enqueueJobCodeImportMutation = /* GraphQL */ `
  mutation EnqueueJobCodeImport($input: EnqueueJobCodeImportInput!) {
    enqueueJobCodeImport(input: $input) {
      statusCode
      body
    }
  }
`;

const enqueueFlexCodeImportMutation = /* GraphQL */ `
  mutation EnqueueFlexCodeImport($input: EnqueueFlexCodeImportInput!) {
    enqueueFlexCodeImport(input: $input) {
      statusCode
      body
    }
  }
`;

// SEE-190: read side of the job-code import pipeline, served by the CDK
// get-job-codes Lambda. Same selection the Amplify `getJobCodes` @function
// returned, minus `paidolUsersMap`, which nothing consumed (spec D4).
const getJobCodesQuery = /* GraphQL */ `
  query GetJobCodes($input: GetJobCodesInput!) {
    getJobCodes(input: $input) {
      jobCodeImport {
        id
        paidolId
        authPaidolId
        createdAt
        updatedAt
        status
        errors
        storageFileKey
        fileName
        jobCodes {
          nextToken
          items {
            id
            code
            description
            budget
            isActive
            jobCodeImportId
            paidolId
            authPaidolId
            createdAt
            updatedAt
            spent {
              amount
              currency
            }
          }
        }
      }
    }
  }
`;

// --- Input Types ---

export type FlexCodeImportTypeInput = 'BASIC' | 'OPTIMIZED';

export interface EnqueueJobCodeImportInput {
  paidolId: string;
  storageFileKey: string;
  fileName: string;
}

export interface EnqueueFlexCodeImportInput {
  paidolId: string;
  storageFileKey: string;
  fileName: string;
  type: FlexCodeImportTypeInput;
}

export interface EnqueueImportResult {
  id: string;
  paidolId: string;
  status: string;
  createdAt: string;
  type?: FlexCodeImportTypeInput;
}

export interface InviteUserToPaidolInput {
  id?: string;
  paidol_id: string;
  user_id?: string;
  email: string;
  employee_id?: number;
  position?: string;
  phone?: string;
  roles?: 'ADMINISTRATOR' | 'GROUP_ADMINISTRATOR' | 'CARDHOLDER' | 'MEMBER';
}

export interface CreateOrGetPaidolSessionInput {
  sessionId: string;
  paidolId: string;
  userId: string;
}

export interface GenerateStatementInput {
  paidol_id: string;
}

export interface SetThresholdAmountInput {
  paidolId: string;
  lowBalanceAlertThreshold?: number | null;
  lowBalanceAutoFundingAmount?: number | null;
  lowBalanceAutoFundingThreshold?: number | null;
}

export interface CheckPaidolSessionInput {
  sessionId: string;
  paidolId: string;
}

export interface SearchStatementSummariesInput {
  filter: {
    paidolId: string;
    startDateFrom?: string;
    startDateTo?: string;
  };
  nextToken?: string;
  limit?: number;
  sortDirection?: 'ASC' | 'DESC';
}

export interface PriorityActionsInput {
  paidolId: string;
  userId: string;
}

// --- updatePaidolSettings input shape (SEE-857 D3) ---
//
// Field union of every paidol-level admin-controlled setting that used to be
// written via auto-gen `updatePaidol`. paidolId routes assert_admin and
// identifies the target row; every other field is optional. The CDK Lambda
// strict-rejects any input key outside this set with a 400.

export interface ToggleableSettingInput {
  isEnabled: boolean;
}

export interface ReimbursementSettingsInput {
  isEnabled: boolean;
  isCheckrunEnabled?: boolean;
  mileageRate?: number | null;
}

export interface ReceiptsSettingsInput {
  isEnabled: boolean;
  minimumAmount?: number | null;
  exemptCardIds?: string[];
}

export interface FuelCaptureSettingsInput {
  isEnabled: boolean;
  isVehicleIdRequired?: boolean;
  isGallonsRequired?: boolean;
  isOdometerRequired?: boolean;
  isMCC5542Required?: boolean;
  isMCC5541Required?: boolean;
}

export interface EditLockingSettingsInput {
  isEnabled?: boolean;
}

export interface ApprovalWorkflowSettingsInput {
  /** Optional: the backend merges a partial payload onto the saved settings. */
  isEnabled?: boolean;
  approvalLevels?: number;
  groupApprovalsEnabled?: boolean;
}

export interface CardLockSettingsInput {
  isEnabled: boolean;
  daysToLock?: number;
  isReceiptsRequired?: boolean;
  isNotesRequired?: boolean;
  isFuelCaptureRequired?: boolean;
  applyImmediately?: boolean;
  disableAutoUnlock?: boolean;
  includePendingCharges?: boolean;
}

// SEE-219: org-level inactivity session timeout. The idle timer is always on
// (platform default 24h); enabling this customizes the duration for everyone
// in the paidol. Effective per-platform min/max/default are flag-driven
// (`sessiontimeoutconfig`); the server sanity-validates to [5, 43200].
export interface SessionTimeoutSettingsInput {
  isEnabled: boolean;
  timeoutMinutes?: number;
}

export interface ERPProjectsEndpointSettingInput {
  selectedEndpoint?: string;
  isEnabled?: boolean;
  shouldDisplay?: boolean;
}

export interface ERPDepartmentLocationSettingInput {
  isEnabled: boolean;
  departmentEnabled?: boolean;
  locationEnabled?: boolean;
}

export interface PaidolFilterSettingsInput {
  isAllAvailable: boolean;
  ids?: string[];
}

export interface CodingColumnSettingInput {
  visibility?: 'DISABLED' | 'ADMINISTRATOR' | 'GROUP_ADMINISTRATOR' | 'CARDHOLDER';
  order?: number | null;
  customLabel?: string | null;
  defaultValue?: { id: string; label: string } | null;
  isRequired?: boolean;
}

export interface CodingColumnsConfigInput {
  isEnabled?: boolean;
  glCode?: CodingColumnSettingInput;
  project?: CodingColumnSettingInput;
  costCode?: CodingColumnSettingInput;
  costType?: CodingColumnSettingInput;
  vendor?: CodingColumnSettingInput;
  subJob?: CodingColumnSettingInput;
  location?: CodingColumnSettingInput;
  department?: CodingColumnSettingInput;
  employee?: CodingColumnSettingInput;
  accountingDate?: CodingColumnSettingInput;
  subaccount?: CodingColumnSettingInput;
  jobCode?: CodingColumnSettingInput;
  // SEE-1362 — Sage Intacct per-transaction billable item column (SEE-1135)
  billableItem?: CodingColumnSettingInput;
  // SEE-974 — Vista equipment coding columns. transactionType is a pinned-first,
  // non-reorderable column; it persists visibility/label/required like the others
  // (its value is derived, so it's omitted from required-field enforcement).
  transactionType?: CodingColumnSettingInput;
  equipment?: CodingColumnSettingInput;
  emCostCode?: CodingColumnSettingInput;
  emCostType?: CodingColumnSettingInput;
}

export interface CodingRequirementGroupInput {
  columns: string[];
}

export interface CodingRequirementsInput {
  isEnabled?: boolean;
  groups?: CodingRequirementGroupInput[];
}

export interface UpdatePaidolSettingsInput {
  paidolId: string;
  // Toggle-only settings
  jobCodeSettings?: ToggleableSettingInput;
  flexCodingSettings?: ToggleableSettingInput;
  flexFieldSettings?: ToggleableSettingInput;
  notesSettings?: ToggleableSettingInput;
  accountingDateSettings?: ToggleableSettingInput;
  qboPostingSettings?: ToggleableSettingInput;
  sage300PostingSettings?: ToggleableSettingInput;
  sageIntacctBillableSettings?: ToggleableSettingInput;
  glCodeFilteringByUser?: ToggleableSettingInput;
  projectFilteringByUser?: ToggleableSettingInput;
  locationFilteringByUser?: ToggleableSettingInput;
  departmentFilteringByUser?: ToggleableSettingInput;
  erpCostCodesUseDescriptionSetting?: ToggleableSettingInput;
  // SEE-1325: order every ERP coding dropdown by code/number instead of name
  erpDropdownSortByCodeSetting?: ToggleableSettingInput;
  receiptAgentSettings?: ToggleableSettingInput;
  requireProjectBeforeCostCode?: ToggleableSettingInput;
  // SEE-1435: scope equipment cost codes/types to the equipment's EM Department.
  scopeEquipmentCodesToEmDepartment?: ToggleableSettingInput;
  // SEE-1763: QBO opt-in to attempt a post with any coded field
  qboAnyCodingPostingSettings?: ToggleableSettingInput;
  // Compound shapes
  reimbursementSettings?: ReimbursementSettingsInput;
  receiptsSettings?: ReceiptsSettingsInput;
  fuelCaptureSettings?: FuelCaptureSettingsInput;
  cardLockSettings?: CardLockSettingsInput;
  sessionTimeoutSettings?: SessionTimeoutSettingsInput;
  approvalWorkflowSettings?: ApprovalWorkflowSettingsInput;
  editLockingSettings?: EditLockingSettingsInput;
  erpProjectsEndpointSetting?: ERPProjectsEndpointSettingInput;
  erpDepartmentLocationSetting?: ERPDepartmentLocationSettingInput;
  // Visibility maps
  glCodeVisibilitySettings?: PaidolFilterSettingsInput;
  projectVisibilitySettings?: PaidolFilterSettingsInput;
  locationVisibilitySettings?: PaidolFilterSettingsInput;
  // Coding columns config
  codingColumnsConfig?: CodingColumnsConfigInput;
  codingRequirements?: CodingRequirementsInput;
  // Agave integration scalars
  agaveAccountToken?: string;
  agaveSourceSystemSlug?: string;
  // SEE-1112: customer declares no Sage Intacct Projects module — coding is
  // sourced from GL dimensions (Class -> cost code, Item -> cost type).
  intacctProjectsNotLicensed?: boolean;
  // SEE-1773: post top-level-created Sage Intacct projects on the top-level session.
  intacctTopLevelProjectPosting?: boolean;
}

// --- Response Types ---

export interface BasicHTTPResponse {
  statusCode: number | null;
  body: string | null;
}

export interface GenerateStatementPayload {
  s3_key: string | null;
  paidol_id: string | null;
  period_start: string | null;
  period_end: string | null;
  message: string | null;
  error: string | null;
}

export interface StatusCodeResponse {
  statusCode: number;
  message: string;
}

export interface StatementSummary {
  id: string;
  paidolId: string;
  startDate: string;
  endDate: string;
  statementBalance: number;
  paymentDueDate: string | null;
  pdfLink: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SearchStatementSummariesPayload {
  items: StatementSummary[];
  nextToken: string | null;
  total: number | null;
}

export interface PaidolUserPayload {
  id: string;
  paidol_id: string | null;
  email: string | null;
  roles: string | null;
  user_id: string | null;
  employee_id: number | null;
  authorizedUserId: string | null;
  authorizedUserApplicationId: string | null;
  highnotePaymentCardId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
}

export interface PriorityActions {
  newMessages: number | null;
  inactiveCards: number | null;
  unactivatedCards: number | null;
  reimbursements: number | null;
  missingJobCodes: number | null;
  missingReceipts: number | null;
  missingNotes: number | null;
}

// --- Service ---

/**
 * Service for v2Paidol operations via the CDK AppSync API.
 *
 * Follows the same pattern as AgaveSyncService: uses fetch() + Cognito JWT
 * to call the CDK-managed AppSync endpoint (REACT_APP_CDK_APPSYNC_ENDPOINT).
 */
export class PaidolService {
  private readonly cdkAppSyncEndpoint: string;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

    if (!this.cdkAppSyncEndpoint) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_CDK_APPSYNC_ENDPOINT is not configured');
    }
  }

  async registerOnboard(input: RegisterOnboardInput): Promise<string | null> {
    const result = await this.execute<{ registerOnboard: string | null }>(
      registerOnboardMutation,
      { input },
      'registerOnboard',
      'iam'
    );
    return result.registerOnboard;
  }

  // --- SEE-1274 onboarding OTP session ---

  async startOnboardingSession(
    input: StartOnboardingSessionInput
  ): Promise<OnboardingSessionChallenge | null> {
    const result = await this.execute<{ startOnboardingSession: OnboardingSessionChallenge | null }>(
      startOnboardingSessionMutation,
      { input },
      'startOnboardingSession',
      'iam'
    );
    return result.startOnboardingSession;
  }

  async bindOnboardingPhone(input: BindOnboardingPhoneInput): Promise<OnboardingSessionChallenge | null> {
    const result = await this.execute<{ bindOnboardingPhone: OnboardingSessionChallenge | null }>(
      bindOnboardingPhoneMutation,
      { input },
      'bindOnboardingPhone',
      'iam'
    );
    return result.bindOnboardingPhone;
  }

  async verifyOnboardingCode(
    input: VerifyOnboardingCodeInput
  ): Promise<OnboardingSessionVerifyResult | null> {
    const result = await this.execute<{
      verifyOnboardingCode: OnboardingSessionVerifyResult | null;
    }>(verifyOnboardingCodeMutation, { input }, 'verifyOnboardingCode', 'iam');
    return result.verifyOnboardingCode;
  }

  async getOnboardingDraft(input: GetOnboardingDraftInput): Promise<OnboardingDraftPayload | null> {
    const result = await this.execute<{ getOnboardingDraft: OnboardingDraftPayload | null }>(
      getOnboardingDraftQuery,
      { input },
      'getOnboardingDraft',
      'iam'
    );
    return result.getOnboardingDraft;
  }

  async listProducts(): Promise<OnboardingProduct[] | null> {
    const result = await this.execute<{ listProducts: OnboardingProduct[] | null }>(
      listProductsQuery,
      {},
      'listProducts',
      'iam'
    );
    return result.listProducts;
  }

  async saveOnboardingDraft(input: SaveOnboardingDraftInput): Promise<OnboardingDraftSaveResult | null> {
    const result = await this.execute<{ saveOnboardingDraft: OnboardingDraftSaveResult | null }>(
      saveOnboardingDraftMutation,
      { input },
      'saveOnboardingDraft',
      'iam'
    );
    return result.saveOnboardingDraft;
  }

  async inviteUser(
    input: InviteUserToPaidolInput,
    hostname: string,
    highnotePaymentCardId?: string
  ): Promise<PaidolUserPayload | null> {
    const result = await this.execute<{ inviteUser: PaidolUserPayload }>(
      inviteUserMutation,
      { input, hostname, highnotePaymentCardId },
      'inviteUser'
    );
    return result.inviteUser ?? null;
  }

  async createOrGetPaidolSession(input: CreateOrGetPaidolSessionInput): Promise<BasicHTTPResponse> {
    const result = await this.execute<{ createOrGetPaidolSession: BasicHTTPResponse }>(
      createOrGetPaidolSessionMutation,
      { input },
      'createOrGetPaidolSession'
    );
    return result.createOrGetPaidolSession;
  }

  async generateStatements(input: GenerateStatementInput): Promise<GenerateStatementPayload> {
    const result = await this.execute<{ generateStatements: GenerateStatementPayload }>(
      generateStatementsMutation,
      { input },
      'generateStatements'
    );
    return result.generateStatements;
  }

  async setThresholdAmount(input: SetThresholdAmountInput): Promise<PaidolUserPayload | null> {
    const result = await this.execute<{ setThresholdAmount: PaidolUserPayload }>(
      setThresholdAmountMutation,
      { input },
      'setThresholdAmount'
    );
    return result.setThresholdAmount ?? null;
  }

  async checkPaidolSession(input: CheckPaidolSessionInput): Promise<StatusCodeResponse> {
    const result = await this.execute<{ checkPaidolSession: StatusCodeResponse }>(
      checkPaidolSessionQuery,
      { input },
      'checkPaidolSession'
    );
    return result.checkPaidolSession;
  }

  async searchStatementSummaries(
    input: SearchStatementSummariesInput
  ): Promise<SearchStatementSummariesPayload> {
    const result = await this.execute<{ v2SearchStatementSummaries: SearchStatementSummariesPayload }>(
      v2SearchStatementSummariesQuery,
      { input },
      'v2SearchStatementSummaries'
    );
    return result.v2SearchStatementSummaries;
  }

  async getPriorityActions(input: PriorityActionsInput): Promise<PriorityActions> {
    const result = await this.execute<{ getPriorityActions: PriorityActions }>(
      getPriorityActionsQuery,
      { input },
      'getPriorityActions'
    );
    return result.getPriorityActions;
  }

  async v2PaidolHealthCheck(input: string): Promise<string> {
    const result = await this.execute<{ v2PaidolHealthCheck: string }>(
      v2PaidolHealthCheckQuery,
      { input },
      'v2PaidolHealthCheck'
    );
    return result.v2PaidolHealthCheck;
  }

  /**
   * SEE-857 D3 — admin-only write to paidol-level settings (every field that
   * used to be writable via auto-gen `updatePaidol`). Throws on 4xx with the
   * body string as error message; returns the updated Paidol dict (parsed
   * from BasicHTTPResponse.body) on success.
   */
  async updatePaidolSettings(input: UpdatePaidolSettingsInput): Promise<Record<string, unknown>> {
    const result = await this.execute<{ updatePaidolSettings: BasicHTTPResponse }>(
      updatePaidolSettingsMutation,
      { input },
      'updatePaidolSettings'
    );
    const payload = result.updatePaidolSettings;
    if (!payload || payload.statusCode == null) {
      throw new Error('updatePaidolSettings returned no payload');
    }
    if (payload.statusCode !== 200) {
      throw new Error(payload.body || `updatePaidolSettings failed (${payload.statusCode})`);
    }
    if (!payload.body) {
      return {};
    }
    return JSON.parse(payload.body) as Record<string, unknown>;
  }

  /**
   * SEE-857 D6 — paidol-member-asserted import enqueue. Replaces the auto-gen
   * `createJobCodeImport` mutation. Returns the new import row's id + status.
   */
  async enqueueJobCodeImport(input: EnqueueJobCodeImportInput): Promise<EnqueueImportResult> {
    const result = await this.execute<{ enqueueJobCodeImport: BasicHTTPResponse }>(
      enqueueJobCodeImportMutation,
      { input },
      'enqueueJobCodeImport'
    );
    return this.parseEnqueueImportResponse(result.enqueueJobCodeImport, 'enqueueJobCodeImport');
  }

  /**
   * SEE-857 D6 — paidol-member-asserted import enqueue. Replaces the auto-gen
   * `createFlexCodeImport` mutation.
   */
  async enqueueFlexCodeImport(input: EnqueueFlexCodeImportInput): Promise<EnqueueImportResult> {
    const result = await this.execute<{ enqueueFlexCodeImport: BasicHTTPResponse }>(
      enqueueFlexCodeImportMutation,
      { input },
      'enqueueFlexCodeImport'
    );
    return this.parseEnqueueImportResponse(result.enqueueFlexCodeImport, 'enqueueFlexCodeImport');
  }

  /**
   * SEE-190 — the paidol's most recent COMPLETED job-code import with one page of
   * its codes and each code's month-to-date spend. Member-asserted. `nextToken`
   * is opaque and only valid against this API. Returns null when the paidol has
   * no completed import.
   */
  async getJobCodes(input: GetJobCodesInput): Promise<GetJobCodesPayloadResult | null> {
    const result = await this.execute<{ getJobCodes: GetJobCodesPayloadResult | null }>(
      getJobCodesQuery,
      { input },
      'getJobCodes'
    );
    return result.getJobCodes;
  }

  /**
   * SEE-1232 row n — replaces the Amplify auto-gen `getPaidol` read behind the company
   * switcher. The caller keeps the `API` `Paidol` type, so the CDK payload is bridged
   * here rather than in the slice (the same boundary card.service.ts uses for CardGroup).
   */
  async getPaidol<T>(id: string): Promise<T | null> {
    const result = await this.execute<{ getPaidol: T | null }>(getPaidolForSwitcher, { id }, 'getPaidol');
    return result.getPaidol ?? null;
  }

  /**
   * SEE-1232 row n — the agave-token read. A separate two-field document rather than
   * reusing getPaidol(), so the Agave path does not pull the full 50-field selection.
   */
  async getPaidolAgaveToken(id: string): Promise<string | null | undefined> {
    const result = await this.execute<{ getPaidol?: { agaveAccountToken?: string | null } }>(
      getPaidolAgaveTokenQuery,
      { id },
      'getPaidolAgaveToken'
    );
    return result.getPaidol?.agaveAccountToken;
  }

  /**
   * SEE-1232 row o — auto-payments. Replaces the Amplify auto-gen ops; thunk signatures
   * and resolved shapes are unchanged.
   */
  async listAutoPaymentsByPaidolId<T>(paidolId: string): Promise<T[]> {
    const result = await this.execute<{ listAutoPaymentsByPaidolId: { items: T[] } | null }>(
      listAutoPaymentsByPaidolIdQuery,
      { paidolId },
      'listAutoPaymentsByPaidolId'
    );
    return result.listAutoPaymentsByPaidolId?.items ?? [];
  }

  async createAutoPayment<T>(input: Record<string, unknown>): Promise<T | undefined> {
    const result = await this.execute<{ createAutoPayment: T }>(
      createAutoPaymentMutation,
      { input },
      'createAutoPayment'
    );
    return result.createAutoPayment;
  }

  async updateAutoPayment<T>(input: Record<string, unknown>): Promise<T | undefined> {
    const result = await this.execute<{ updateAutoPayment: T }>(
      updateAutoPaymentMutation,
      { input },
      'updateAutoPayment'
    );
    return result.updateAutoPayment;
  }

  /**
   * SEE-1232 row p — the Highnote onboarding integration record. Update only; create and
   * delete exist on neither API by deliberate design (SEE-861 / SEE-856).
   */
  async getPaidolHighnoteIntegration<T>(id: string): Promise<T | undefined> {
    const result = await this.execute<{ getPaidolHighnoteIntegration: T }>(
      getPaidolHighnoteIntegrationQuery,
      { id },
      'getPaidolHighnoteIntegration'
    );
    return result.getPaidolHighnoteIntegration;
  }

  async updatePaidolHighnoteIntegration<T>(input: Record<string, unknown>): Promise<T | undefined> {
    const result = await this.execute<{ updatePaidolHighnoteIntegration: T }>(
      updatePaidolHighnoteIntegrationMutation,
      { input },
      'updatePaidolHighnoteIntegration'
    );
    return result.updatePaidolHighnoteIntegration;
  }

  /**
   * SEE-272 — Control Center credit limit for one company. Support users only; amounts are
   * cents. `highnoteReadFailed` means Highnote couldn't be reached (its values are null), as
   * opposed to the company having no Highnote account.
   */
  async getCreditLimitAsSupport(paidolId: string): Promise<SupportCreditLimit | null> {
    const result = await this.execute<{ getCreditLimitAsSupport: SupportCreditLimit | null }>(
      getCreditLimitAsSupportQuery,
      { paidolId },
      'getCreditLimitAsSupport'
    );
    return result.getCreditLimitAsSupport ?? null;
  }

  /** SEE-272 — copy Highnote's limit into Speedchain. The server reads Highnote itself. */
  async pullCreditLimitFromHighnote(paidolId: string): Promise<SupportCreditLimit | null> {
    const result = await this.execute<{ pullCreditLimitFromHighnote: SupportCreditLimit | null }>(
      pullCreditLimitFromHighnoteMutation,
      { paidolId },
      'pullCreditLimitFromHighnote'
    );
    return result.pullCreditLimitFromHighnote ?? null;
  }

  /** SEE-272 — set the Speedchain limit by hand, in whole cents. May differ from Highnote's. */
  async setCreditLimitAsSupport(paidolId: string, creditLimit: number): Promise<SupportCreditLimit | null> {
    const result = await this.execute<{ setCreditLimitAsSupport: SupportCreditLimit | null }>(
      setCreditLimitAsSupportMutation,
      { paidolId, creditLimit },
      'setCreditLimitAsSupport'
    );
    return result.setCreditLimitAsSupport ?? null;
  }

  /** Organization → Audit log: one page of a company's audit entries. */
  async listAuditLogEvents(input: ListAuditLogEventsInput): Promise<AuditLogEventPage> {
    const result = await this.execute<{ listAuditLogEvents: AuditLogEventPage }>(
      listAuditLogEventsQuery,
      { input },
      'listAuditLogEvents'
    );
    return result.listAuditLogEvents;
  }

  private parseEnqueueImportResponse(
    response: BasicHTTPResponse | undefined,
    operationName: string
  ): EnqueueImportResult {
    if (!response || response.statusCode == null) {
      throw new Error(`${operationName} returned no payload`);
    }
    if (response.statusCode !== 200) {
      throw new Error(response.body || `${operationName} failed (${response.statusCode})`);
    }
    if (!response.body) {
      throw new Error(`${operationName} returned an empty body`);
    }
    return JSON.parse(response.body) as EnqueueImportResult;
  }

  // --- Private helpers ---

  private async execute<T>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string,
    authMode: AuthMode = 'cognito'
  ): Promise<T> {
    if (!this.cdkAppSyncEndpoint) {
      captureException(new Error('CDK AppSync endpoint is not configured'), {
        tags: { area: 'paidol', type: 'config' },
        extra: { envVar: 'REACT_APP_CDK_APPSYNC_ENDPOINT' },
      });
      throw new Error('Service is currently unavailable. Please contact support.');
    }

    try {
      const body = JSON.stringify({ query, variables });
      let response: Response;

      if (authMode === 'iam') {
        response = await this.executeWithIamAuth(body);
      } else {
        const session = await Auth.currentSession();
        const jwt = session.getIdToken().getJwtToken();
        response = await fetchWithTransportRetry(this.cdkAppSyncEndpoint, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: jwt,
          },
          body,
        });
      }

      // Rate limiting is partly enforced at the edge, which answers with a plain 403 and an
      // HTML/text body rather than a GraphQL error envelope. Parsing that as JSON threw a
      // SyntaxError that told the applicant nothing (Req 2.4).
      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.toLowerCase().includes('json')) {
        if (response.status === 403 || response.status === 429) {
          throw new Error('Too many requests. Please wait a moment and try again.');
        }
        // Status and content-type only. This error reaches Sentry below, and the bodies that land
        // here are WAF/ALB/CloudFront error pages — they carry internal hostnames and occasionally
        // stack traces, none of which help diagnose a call the client already knows failed.
        throw new Error(
          `${operationName} failed: HTTP ${response.status} (${contentType || 'no content-type'})`
        );
      }

      const json = (await response.json()) as GraphQLResult<T> & {
        errors?: Array<{ message: string }>;
      };

      reportCdkRefusal(json?.errors, { authMode, operationName });

      // SEE-1388: keep Highnote's field rejections typed so the onboarding form can point at the field.
      const highnoteFieldErrors = highnoteFieldErrorsOf(json?.errors);
      if (highnoteFieldErrors) {
        throw new HighnoteValidationError(
          json.errors?.map((e) => e.message).join(', ') || `${operationName} was rejected`,
          highnoteFieldErrors
        );
      }

      if (!response.ok) {
        const message = json?.errors?.map((e) => e.message).join(', ') || `HTTP ${response.status}`;
        throw new Error(message);
      }

      if (json.errors && json.errors.length > 0) {
        const errorMessage = json.errors.map((e) => e.message).join(', ');
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      if (!json.data) {
        throw new Error('No data returned from operation');
      }

      return json.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to execute ${operationName}:`, error);

      if (error instanceof Error) {
        captureException(error, {
          tags: { area: 'paidol', type: isNetworkTransportError(error) ? 'network-transport' : 'graphql' },
          extra: { operation: operationName, endpoint: this.cdkAppSyncEndpoint },
        });
        throw error;
      }

      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  private async executeWithIamAuth(body: string): Promise<Response> {
    const credentials = await Auth.currentCredentials();
    const { accessKeyId, secretAccessKey, sessionToken } = credentials;

    const url = new URL(this.cdkAppSyncEndpoint);
    // Extract region from AppSync endpoint: *.appsync-api.{region}.amazonaws.com
    const regionMatch = url.hostname.match(/\.appsync-api\.([^.]+)\./);
    const region = regionMatch?.[1] ?? 'us-east-2';

    const request = {
      method: 'POST',
      url: this.cdkAppSyncEndpoint,
      headers: {
        'content-type': 'application/json',
        host: url.host,
      },
      data: body,
    };

    const signedRequest = Signer.sign(
      request,
      { access_key: accessKeyId, secret_key: secretAccessKey, session_token: sessionToken },
      { region, service: 'appsync' }
    );

    return fetchWithTransportRetry(this.cdkAppSyncEndpoint, {
      method: 'POST',
      headers: signedRequest.headers,
      body,
    });
  }
}

// Export a singleton instance for convenience
export const paidolService = new PaidolService();
